/**
 * Tab Manager
 * Handles tab creation, switching, and workspace visibility
 */

import { createTerminal, destroyTerminal } from './terminal.js';
import { createEditor, destroyEditor, saveEditor, isEditorDirty, setEditorReadOnly, isEditorReadOnly } from './editor.js';
import { debug } from '../utils/debug.js';
import { showConfirmDialog } from '../utils/ui.js';

// Tab types and their workspace mappings
const TAB_TYPES = {
    files: { icon: 'folder_open', workspacePrefix: 'workspace-file', closeable: false },
    terminal: { icon: 'terminal', workspacePrefix: 'workspace-terminal', closeable: true },
    editor: { icon: 'edit', workspacePrefix: 'workspace-editor', closeable: true }
};

let tabIdCounter = 0;
let tabListenersInitialized = false;

/**
 * Initialize Tab Manager
 * Renders initial tabs and sets up event listeners
 */
export function initTabManager() {
    renderTabs();
    setupTabListeners();
    setupWorkspaceCloseButtons();
    
    // Show initial active workspace
    showWorkspace(window.appState.activeTabId);
    
    debug.log('🗂️ Tab Manager initialized');
}

/**
 * Render all tabs from appState
 * Files tab is static in HTML, we only manage dynamic tabs (terminal, editor)
 */
export function renderTabs() {
    const container = document.querySelector('.tab-dock-outline');
    if (!container) {
        console.error('renderTabs: .tab-dock-outline not found!');
        return;
    }
    
    debug.log('renderTabs: Rendering', window.appState.openTabs.length, 'tabs');
    
    // Update Files tab active state
    const filesTab = document.getElementById('tab-files');
    if (filesTab) {
        filesTab.classList.toggle('active', window.appState.activeTabId === 'files');
    }
    
    // Remove old dynamic tabs (keep Files tab)
    const dynamicTabs = container.querySelectorAll('.tab-item:not(#tab-files)');
    dynamicTabs.forEach(tab => tab.remove());
    
    // Add dynamic tabs (terminal, editor)
    window.appState.openTabs.forEach(tab => {
        if (tab.type === 'files') return; // Skip Files, it's static
        
        const isActive = tab.id === window.appState.activeTabId;
        const config = TAB_TYPES[tab.type] || TAB_TYPES.editor;
        
        const tabElement = document.createElement('div');
        tabElement.className = `tab-item ${isActive ? 'active' : ''}`;
        tabElement.dataset.tabId = tab.id;
        
        tabElement.innerHTML = `
            <button class="tab">
                <span class="icon icon-sm">${tab.icon || config.icon}</span>
                <span class="tab-text">${tab.label}</span>
            </button>
            ${config.closeable ? `<span class="tab-close icon icon-sm" data-tab-id="${tab.id}">cancel</span>` : ''}
        `;
        
        container.appendChild(tabElement);
    });
    
}

/**
 * Setup click listeners on tabs
 */
function setupTabListeners() {
    if (tabListenersInitialized) return;

    const container = document.querySelector('.tab-dock-outline');
    if (!container) return;

    container.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('.tab-close');
        if (closeBtn) {
            e.stopPropagation();
            const tabId = closeBtn.dataset.tabId;
            if (tabId) closeTab(tabId);
            return;
        }

        const tabItem = e.target.closest('.tab-item');
        if (!tabItem) return;

        const tabId = tabItem.dataset.tabId;
        if (tabId) switchTab(tabId);
    });

    tabListenersInitialized = true;
}

/**
 * Switch to a tab
 * @param {string} tabId 
 */
export function switchTab(tabId) {
    const tab = window.appState.openTabs.find(t => t.id === tabId);
    if (!tab) return;
    
    window.appState.activeTabId = tabId;
    
    // Update tab UI
    document.querySelectorAll('.tab-item').forEach(item => {
        item.classList.toggle('active', item.dataset.tabId === tabId);
    });
    
    // Show corresponding workspace
    showWorkspace(tabId);
    
    // Lazy-load terminal/editor when tab becomes active
    initializeWorkspaceContent(tabId);
}

/**
 * Show workspace for a tab, hide others
 * @param {string} tabId 
 */
function showWorkspace(tabId) {
    const tab = window.appState.openTabs.find(t => t.id === tabId);
    if (!tab) {
        console.warn('showWorkspace: Tab not found:', tabId);
        return;
    }

    let workspaceId;
    if (tab.type === 'files') {
        workspaceId = 'workspace-file';
    } else if (tab.type === 'terminal') {
        workspaceId = 'workspace-terminal';
    } else if (tab.type === 'editor') {
        workspaceId = `workspace-editor-${tab.id}`;
    }

    // Hide all workspaces via class toggle
    const allStatic = ['workspace-file', 'workspace-terminal'];
    allStatic.forEach(id => {
        const el = document.getElementById(id);
        if (el) {
            el.classList.add('workspace-hidden');
            el.classList.remove('workspace-visible');
        }
    });
    document.querySelectorAll('[id^="workspace-editor-"]').forEach(ws => {
        ws.classList.add('workspace-hidden');
        ws.classList.remove('workspace-visible');
    });

    // Show target workspace
    const target = document.getElementById(workspaceId);
    if (target) {
        target.classList.remove('workspace-hidden');
        target.classList.add('workspace-visible');
    } else {
        console.error('Workspace not found:', workspaceId);
        const filesWorkspace = document.getElementById('workspace-file');
        if (filesWorkspace) {
            filesWorkspace.classList.remove('workspace-hidden');
            filesWorkspace.classList.add('workspace-visible');
        }
    }
}

/**
 * Open a new tab (or switch to existing)
 * @param {string} type - 'files', 'terminal', 'editor'
 * @param {object} options - { label, path, icon }
 * @returns {string} tab ID
 */
export function openTab(type, options = {}) {
    debug.log('openTab called:', type, options);
    
    // For terminal, only allow one per machine (singleton)
    if (type === 'terminal') {
        const existing = window.appState.openTabs.find(t => t.type === 'terminal');
        if (existing) {
            debug.log('Terminal already exists, switching to:', existing.id);
            switchTab(existing.id);
            return existing.id;
        }
    }
    
    // For editor, check if file already open
    if (type === 'editor' && options.path) {
        const existing = window.appState.openTabs.find(t => t.type === 'editor' && t.path === options.path);
        if (existing) {
            debug.log('Editor already exists for path, switching to:', existing.id);
            switchTab(existing.id);
            return existing.id;
        }
    }
    
    // Create new tab
    const id = type === 'files' ? 'files' : `${type}-${++tabIdCounter}`;
    const config = TAB_TYPES[type] || TAB_TYPES.editor;
    
    const newTab = {
        id,
        type,
        label: options.label || (type === 'terminal' ? 'Terminal' : 'Untitled'),
        icon: options.icon || config.icon,
        path: options.path || null,
        isNew: !!options.isNew,
        active: true,
        initialized: false, // Track if terminal/editor is loaded
    };
    
    debug.log('Creating new tab:', newTab);
    window.appState.openTabs.push(newTab);
    
    // Create workspace container for editor tabs
    if (type === 'editor') {
        createEditorWorkspace(newTab);
    }
    
    renderTabs();
    switchTab(id);
    
    debug.log('Tabs after open:', window.appState.openTabs);
    return id;
}

/**
 * Close a tab
 * @param {string} tabId 
 */
export async function closeTab(tabId) {
    const tabIndex = window.appState.openTabs.findIndex(t => t.id === tabId);
    if (tabIndex === -1) return;
    
    const tab = window.appState.openTabs[tabIndex];
    const config = TAB_TYPES[tab.type];
    
    // Don't close non-closeable tabs (Files)
    if (!config?.closeable) return;
    // Check for unsaved changes in editor
    if (tab.type === 'editor' && tab.editorId && isEditorDirty(tab.editorId)) {
        const confirmed = await showConfirmDialog('Unsaved Changes', `File "${tab.label}" has unsaved changes. Close anyway?`);
        if (!confirmed) {
            return;
        }
    }
    
    // Destroy terminal/editor instances
    if (tab.type === 'terminal' && tab.terminalId) {
        destroyTerminal(tab.terminalId);
    } else if (tab.type === 'editor' && tab.editorId) {
        destroyEditor(tab.editorId);
    }
    
    // Remove tab
    window.appState.openTabs.splice(tabIndex, 1);
    
    // Remove workspace container for editors
    if (tab.type === 'editor') {
        const ws = document.getElementById(`workspace-editor-${tabId}`);
        if (ws) ws.remove();
    }
    
    // If closing active tab, switch to previous or files
    if (window.appState.activeTabId === tabId) {
        const newActive = window.appState.openTabs[Math.max(0, tabIndex - 1)];
        window.appState.activeTabId = newActive?.id || 'files';
    }
    
    renderTabs();
    switchTab(window.appState.activeTabId);
}

/**
 * Close all tool tabs (terminal + editor) without per-tab confirmation.
 * Used during machine switch after the user has already confirmed.
 */
export function closeAllToolTabs() {
    const toolTabs = window.appState.openTabs.filter(t => t.type !== 'files');

    toolTabs.forEach(tab => {
        // Destroy terminal/editor instances
        if (tab.type === 'terminal' && tab.terminalId) {
            destroyTerminal(tab.terminalId);
        } else if (tab.type === 'editor' && tab.editorId) {
            destroyEditor(tab.editorId);
        }

        // Remove workspace container for editors
        if (tab.type === 'editor') {
            const ws = document.getElementById(`workspace-editor-${tab.id}`);
            if (ws) ws.remove();
        }
    });

    // Keep only the Files tab
    window.appState.openTabs = window.appState.openTabs.filter(t => t.type === 'files');
    window.appState.activeTabId = 'files';

    renderTabs();
    switchTab('files');

    debug.log('🗂️ All tool tabs closed');
}

/**
 * Check if there are any dirty editor tabs or open terminal tabs
 * @returns {{ hasDirtyEditors: boolean, hasTerminals: boolean }}
 */
export function hasDirtyToolState() {
    const tabs = window.appState.openTabs;
    const hasTerminals = tabs.some(t => t.type === 'terminal');
    const hasDirtyEditors = tabs.some(t =>
        t.type === 'editor' && t.editorId && isEditorDirty(t.editorId)
    );
    return { hasDirtyEditors, hasTerminals };
}

/**
 * Create a workspace container for an editor tab
 * @param {object} tab 
 */
function createEditorWorkspace(tab) {
    const main = document.querySelector('.main-panel');
    const tabDock = document.querySelector('.tab-dock');
    
    if (!main) {
        console.error('createEditorWorkspace: .main-panel not found');
        return;
    }
    if (!tabDock) {
        console.error('createEditorWorkspace: .tab-dock not found');
        return;
    }
    
    debug.log('Creating editor workspace for:', tab.id);
    
    const workspaceId = `workspace-editor-${tab.id}`;
    const editorContentId = `editor-content-${tab.id}`;
    
    const workspace = document.createElement('div');
    workspace.className = 'workspace-container';
    workspace.id = workspaceId;
    workspace.style.display = 'none';
    
    const isReadOnly = tab.path && !tab.isNew && !/\.(md|markdown|html?|xhtml)$/i.test(tab.path);

    workspace.innerHTML = `
        <div class="tool-container">
            <div class="tool-menu-bar">
                <div class="leading-block"><span class="editor-path-label">${tab.path || tab.label}</span></div>
                <div class="trailing-block">
                    <span class="editor-status-message" aria-live="polite"></span>
                    <button class="editor-mode-toggle" title="${isReadOnly ? 'Read-only (Click to Edit)' : 'Edit Mode (Click to Lock)'}">
                        <span class="icon">${isReadOnly ? 'edit_off' : 'edit'}</span>
                    </button>
                    <button class="editor-save" title="Save (Ctrl+S)" ${isReadOnly ? 'disabled' : ''}>
                        <span class="icon">save</span>
                    </button>
                    <button class="editor-close" title="Close" data-tab-id="${tab.id}">
                        <span class="icon">cancel</span>
                    </button>
                </div>
            </div>
            <div class="tool-content editor-content" id="${editorContentId}" data-tab-id="${tab.id}" data-path="${tab.path || ''}">
                <!-- Monaco editor will be mounted here -->
            </div>
        </div>
    `;
    
    // Insert before tab dock
    try {
        main.insertBefore(workspace, tabDock);
        debug.log('✅ Editor workspace created:', workspaceId);
    } catch (error) {
        console.error('❌ Failed to insert workspace:', error);
        // Fallback: append to main
        main.appendChild(workspace);
    }
    
    // Wire close button
    workspace.querySelector('.editor-close')?.addEventListener('click', () => {
        closeTab(tab.id);
    });
}

/**
 * Get the currently active tab
 * @returns {object|null}
 */
export function getActiveTab() {
    return window.appState.openTabs.find(t => t.id === window.appState.activeTabId) || null;
}

/**
 * Setup close buttons on workspace containers (terminal, etc.)
 */
function setupWorkspaceCloseButtons() {
    // Terminal close
    const terminalClose = document.querySelector('.terminal-close');
    if (terminalClose) {
        terminalClose.addEventListener('click', () => {
            const terminalTab = window.appState.openTabs.find(t => t.type === 'terminal');
            if (terminalTab) closeTab(terminalTab.id);
        });
    }
}

/**
 * Initialize terminal/editor content when tab is first activated (lazy loading)
 * Uses synchronous 'initializing' guard to prevent race conditions
 * @param {string} tabId 
 */
async function initializeWorkspaceContent(tabId) {
    const tab = window.appState.openTabs.find(t => t.id === tabId);
    // Check both initialized AND initializing to prevent race conditions
    if (!tab || tab.initialized || tab.initializing) return;
    
    // Set synchronous guard immediately before any async work
    tab.initializing = true;
    
    debug.log('Initializing workspace content for:', tabId, tab.type);
    
    try {
        if (tab.type === 'terminal') {
            // Mount terminal in the terminal-content container
            const containerId = 'terminal-content';
            const terminalId = await createTerminal(containerId);
            if (terminalId) {
                tab.terminalId = terminalId;
                tab.initialized = true;
                debug.log('✅ Terminal initialized:', terminalId);
            }
        } else if (tab.type === 'editor') {
            // Mount editor in the dynamically created workspace
            const containerId = `workspace-editor-${tab.id}`;
            const editorContainer = document.querySelector(`#${containerId} .editor-content`);
            if (editorContainer) {
                const editorId = await createEditor(editorContainer.id || `editor-content-${tab.id}`, tab.path, { readOnly: tab.path && !tab.isNew && !/\.(md|markdown|html?|xhtml)$/i.test(tab.path) });
                if (editorId) {
                    tab.editorId = editorId;
                    tab.initialized = true;
                    debug.log('✅ Editor initialized:', editorId);
                    
                    // Wire up save button
                    const saveBtn = document.querySelector(`#${containerId} .editor-save`);
                    if (saveBtn) {
                        saveBtn.addEventListener('click', () => saveEditor(editorId));
                    }

                    // Wire up mode toggle button
                    const toggleBtn = document.querySelector(`#${containerId} .editor-mode-toggle`);
                    if (toggleBtn) {
                        toggleBtn.addEventListener('click', () => {
                            const currentReadOnly = isEditorReadOnly(editorId);
                            const nextReadOnly = !currentReadOnly;
                            
                            // Toggle state
                            setEditorReadOnly(editorId, nextReadOnly);
                            
                            // Update UI button icon and title
                            const iconEl = toggleBtn.querySelector('.icon');
                            if (iconEl) {
                                iconEl.textContent = nextReadOnly ? 'edit_off' : 'edit';
                            }
                            toggleBtn.title = nextReadOnly ? 'Read-only (Click to Edit)' : 'Edit Mode (Click to Lock)';
                            
                            // Toggle Save button disabled attribute
                            if (saveBtn) {
                                saveBtn.disabled = nextReadOnly;
                            }
                            
                            debug.log(`Editor ${editorId} mode toggled. ReadOnly: ${nextReadOnly}`);
                        });
                    }
                }
            } else {
                console.error('Editor container not found for:', tab.id);
            }
        }
    } catch (error) {
        console.error('❌ Failed to initialize workspace content:', error);
    } finally {
        // Always clear initializing flag after async work completes
        tab.initializing = false;
    }
}
